import { useSchoolDistrictGetActiveSchoolVisitorSignIns } from "@crowdcoursing/api/openapi";
import { EventAvailableOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { skipToken } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { SchoolRsvps } from "~ui/organisms/DataGrid/SchoolRsvps/SchoolRsvps";
import {
  type VisitorManagementActiveVisitorRow,
  VisitorManagementActiveVisitorsSection,
} from "~ui/organisms/VisitorManagementActiveVisitorsSection/VisitorManagementActiveVisitorsSection";
import { VisitorsHomeTemplate } from "~ui/templates/VisitorsHomeTemplate/VisitorsHomeTemplate";

import { useSchoolRsvpsProps } from "../AdminPage/schoolUserManagementHooks";
import { SchoolSignInsLineChart } from "./SchoolSignInsLineChart";
import { lastFirstDisplayNameFromParts } from "./visitorManagementDisplay";
import { VisitorManagementVisitorActions } from "./VisitorManagementVisitorActions";

const tUpcomingVisitors = "Upcoming Visitors";
const tRsvpsSubtitle = "Users coming to upcoming events.";

export function VisitorsHomePage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const { data: activeSchoolVisitors = [], status: activeSchoolVisitorsStatus } =
    useSchoolDistrictGetActiveSchoolVisitorSignIns(schoolId ? { pathParams: { bubbleId: schoolId } } : skipToken);
  const schoolRsvpsProps = useSchoolRsvpsProps(schoolId ?? "");

  const activeVisitorRows = useMemo<VisitorManagementActiveVisitorRow[]>(() => {
    return activeSchoolVisitors.map((v, index) => ({
      contactEmail: v.email,
      contactPhone: v.phoneNumber,
      name: lastFirstDisplayNameFromParts({ firstName: v.firstName, lastName: v.lastName }, "—"),
      rowId: `${v.userId ?? "unknown"}-${v.timestamp ?? "na"}-${index}`,
      signedInAtMs: v.timestamp ? new Date(v.timestamp).getTime() : 0,
    }));
  }, [activeSchoolVisitors]);

  if (!schoolId) {
    return (
      <Typography color="text.secondary" variant="body1">
        Missing school.
      </Typography>
    );
  }

  return (
    <VisitorsHomeTemplate
      activeVisitorsSection={
        <VisitorManagementActiveVisitorsSection rows={activeVisitorRows} status={activeSchoolVisitorsStatus} />
      }
      chartSlot={<SchoolSignInsLineChart schoolId={schoolId} />}
      upcomingVisitorsSection={
        <>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: { sm: 2, xs: 1 },
              mb: 0.5,
            }}
          >
            <EventAvailableOutlined data-variant="page-header" />
            <Typography component="h2" variant="page-header">
              {tUpcomingVisitors}
            </Typography>
          </Box>
          <Typography color="text.secondary" sx={{ mb: 2 }} variant="subtitle1">
            {tRsvpsSubtitle}
          </Typography>
          <Box sx={{ overflow: "auto" }}>
            <SchoolRsvps {...schoolRsvpsProps} />
          </Box>
        </>
      }
      visitorActionsSlot={<VisitorManagementVisitorActions density="default" />}
    />
  );
}
